import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatFilterInfoUnion } from '@airgram/web';
import { TranslateService } from '@ngx-translate/core';
import { TelegramMessengerService } from '@src/app/modules/telegram';
import { EnvService } from '@src/app/modules/env';
import { SessionService } from '@src/app/modules/auth';
import { APP_CONFIG } from '@src/core';
import { AlertService, UserService } from '@src/core/services';
import { capitalizeFirstLetter, reload } from '@src/utils';
import { BaseResizableComponent } from '@src/app/modules/base';

import { FolderConfig } from './types';

const UNIONS_FOLDER_CONFIG = APP_CONFIG.folders.unions;

@Component({
  selector: 'telegram-folders',
  templateUrl: './folders.component.html',
  styleUrls: ['./folders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FoldersComponent extends BaseResizableComponent {
  private readonly messengerService = inject(TelegramMessengerService);
  private readonly userService = inject(UserService);
  private readonly env = inject(EnvService);
  private readonly sessionService = inject(SessionService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);

  currentFolders?: ChatFilterInfoUnion[];
  unionsFolder?: ChatFilterInfoUnion;
  folderLimit = this.messengerService.limits.foldersCount;

  folderConfig?: FolderConfig;

  allChatsIds$ = this.messengerService.allChatsIds$;

  override ngOnInit(): void {
    super.ngOnInit();

    this.init();
  }

  onLogged() {
    this.init();
  }

  onClickCreateFolderButton() {
    if (!this.env.isActiveTab()) {
      this.env.viewManyTabsDialog();
    }

    if (!this.env.isLoggedMessenger()) {
      this.env.viewConfirmLoginMessenger(() => this.reload());
    }

    if (!this.currentFolders || this.currentFolders.length === this.folderLimit || !this.folderConfig) return;

    const allTgChatsIds = this.allChatsIds$.value;
    if (!allTgChatsIds.length) return;

    lastValueFrom(this.userService.getUnionsChatsIds(allTgChatsIds)).then(allUnionsChatsIds => {
      const includedChatIds = allUnionsChatsIds;

      this.messengerService.api
        .createChatFilter({
          _: 'chatFilter',
          title: this.folderConfig?.title,
          iconName: this.folderConfig?.iconName,
          includedChatIds,
        })
        .then(res => {
          if (res.response._ === 'error') return;

          this.alertService.success(
            `${this.translateService.instant('components.telegramFolders.alerts.successes.folder')} "${
              this.folderConfig?.title
            }" ${this.translateService.instant('components.telegramFolders.alerts.successes.folderCreated')}`,
            0,
          );
        });
    });
  }

  onClickEditFolderButton() {
    if (!this.env.isActiveTab()) {
      this.env.viewManyTabsDialog();
    }

    if (!this.env.isLoggedMessenger()) {
      this.env.viewConfirmLoginMessenger(() => this.reload());
    }

    if (!this.currentFolders) return;

    const folder = this.unionsFolder;
    if (!folder) return;

    const allTgChatsIds = this.allChatsIds$.value;
    if (!allTgChatsIds.length) return;

    lastValueFrom(this.userService.getUnionsChatsIds(allTgChatsIds)).then(allUnionsChatsIds => {
      const includedChatIds = allUnionsChatsIds;

      this.messengerService.api
        .editChatFilter(folder.id, {
          _: 'chatFilter',
          title: this.folderConfig?.title,
          iconName: this.folderConfig?.iconName,
          includedChatIds,
        })
        .then(res => {
          if (res.response._ === 'error') return;

          this.alertService.success(
            `${this.translateService.instant('components.telegramFolders.alerts.successes.folder')} "${
              this.folderConfig?.title
            }" ${this.translateService.instant('components.telegramFolders.alerts.successes.folderEdited')}`,
            0,
          );
        });
    });
  }

  onClickDeleteFolderButton(folder: ChatFilterInfoUnion) {
    if (!this.env.isActiveTab()) {
      this.env.viewManyTabsDialog();
    }

    if (!this.env.isLoggedMessenger()) {
      this.env.viewConfirmLoginMessenger(() => this.reload());
    }

    this.messengerService.api.deleteChatFilter(folder.id).then(() => {
      this.alertService.success(
        `${this.translateService.instant('components.telegramFolders.alerts.successes.folder')} "${
          folder.title
        }" ${this.translateService.instant('components.telegramFolders.alerts.successes.folderDeleted')}`,
        0,
      );
    });
  }

  private init() {
    this.folderConfig = UNIONS_FOLDER_CONFIG;
    if (this.sessionService.appConfig?.shortName) {
      this.folderConfig.title = capitalizeFirstLetter(this.sessionService.appConfig.shortName);
    }

    this.messengerService.chatFilters$.pipe(takeUntil(this.destroyed$$)).subscribe(chatFilters => {
      this.currentFolders = chatFilters;

      this.unionsFolder = this.currentFolders?.find(folder => {
        return folder.title.toLowerCase() === this.folderConfig?.title.toLowerCase();
      });

      this.cdr.markForCheck();
    });

    this.cdr.markForCheck();
  }

  private reload() {
    reload();
  }
}
